import { supabase } from '@/integrations/supabase/client';
import { PlanTier, SubscriptionState } from './types';
import { logger } from '@/utils/logger';

/**
 * Check if user is an admin
 */
export async function checkAdminRole(userId: string): Promise<boolean> {
  const { data: roleData } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', userId)
    .eq('role', 'admin')
    .maybeSingle();

  return !!roleData;
}

/**
 * Sync admin tier to database
 */
export async function syncAdminTier(userId: string, email: string | undefined): Promise<void> {
  const { error } = await supabase
    .from('subscribers')
    .upsert({
      user_id: userId,
      email: email,
      subscription_tier: 'Pro',
      subscribed: true,
      is_manual_override: true,
      manual_override_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id' });
  
  if (error) {
    logger.warn('[Subscription] Failed to sync admin tier to database:', error);
  }
}

/**
 * Fetch subscription data from edge function
 */
export async function fetchSubscriptionData(forceRefresh: boolean): Promise<SubscriptionState | null> {
  const { data, error } = await supabase.functions.invoke('check-subscription', {
    body: { forceRefresh }
  });
  
  if (error) {
    logger.error('[Subscription] Error checking subscription:', error);
    return null;
  }

  if (!data) return null;

  return {
    tier: (data.subscription_tier as PlanTier) || 'Plus',
    subscribed: data.subscribed,
    subscriptionEnd: data.subscription_end,
    needsTrial: data.needsTrial || false,
    isTrial: data.is_trial || false,
    trialEnd: data.trial_end || null,
    isLoading: false
  };
}

/**
 * Create a Stripe checkout session
 */
export async function createStripeCheckout(priceId: string): Promise<string | null> {
  const { data, error } = await supabase.functions.invoke('create-checkout', {
    body: { priceId }
  });

  if (error) {
    logger.error('[Subscription] Error creating checkout:', error);
    return null;
  }

  return data?.url || null;
}

/**
 * Create a Stripe customer portal session
 */
export async function createStripePortal(returnUrl: string): Promise<string | null> {
  const { data, error } = await supabase.functions.invoke('customer-portal', {
    body: { returnUrl }
  });

  if (error) {
    logger.error('[Subscription] Error creating portal:', error);
    return null;
  }

  return data?.url || null;
}

/**
 * Update subscription tier in database
 */
export async function updateSubscriptionTier(
  userId: string, 
  email: string | undefined, 
  newTier: PlanTier
): Promise<boolean> {
  const { error } = await supabase
    .from('subscribers')
    .upsert({
      user_id: userId,
      email: email,
      subscription_tier: newTier,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id' });

  if (error) {
    logger.error('[Subscription] Error setting tier:', error);
    return false;
  }

  return true;
}
